import type { Caps, Role } from '@buildiary/core';
import type { Key } from '../i18n';
import type { IconName } from '../ui/Icon';

type Rule = (c: Caps) => boolean;
export type MenuItem = { id: string; label: Key; path: string; can?: Rule; later?: boolean; icon?: IconName };
export type Menu = { id: string; label: Key; icon: IconName; items: MenuItem[] };

const any: Rule = (c) => Object.values(c).some(Boolean);
const site: Rule = (c) => c.site && !c.partner;
const projects: Rule = (c) => c.site || c.owner || c.money || c.partner || c.approve;
const office: Rule = (c) => (c.site || c.people) && !c.partner;
const people: Rule = (c) => c.people && !c.partner;
const wages: Rule = (c) => (c.people || c.money) && !c.partner;
const store: Rule = (c) => c.store;
const buy: Rule = (c) => c.buy;
const suppliers: Rule = (c) => c.suppliers;
const money: Rule = (c) => c.money;
const clients: Rule = (c) => c.owner || c.approve;
const reports: Rule = (c) => (c.owner || c.money || c.approve || c.site) && !c.partner;
const settings: Rule = (c) => c.settings;

/** The web menu exactly as on the design (Map.dc.html, WebSidebar.dc.html). */
export const MENUS: Menu[] = [
  { id: 'home', label: 'nav.home', icon: 'home', items: [
    { id: 'overview', label: 'nav.home.overview', path: '/home/overview', can: any },
    { id: 'today', label: 'nav.home.today', path: '/home/today', can: (c) => site(c) || c.store || c.owner },
    { id: 'weekly', label: 'nav.home.weekly', path: '/home/weekly', can: (c) => c.owner || c.approve },
  ] },
  { id: 'projects', label: 'nav.projects', icon: 'projects', items: [
    { id: 'all', label: 'nav.projects.all', path: '/projects/all', can: projects },
    { id: 'stages', label: 'nav.projects.stages', path: '/projects/stages', can: projects },
    { id: 'timeline', label: 'nav.projects.timeline', path: '/projects/timeline', can: projects },
    { id: 'files', label: 'nav.projects.files', path: '/projects/files', can: (c) => c.site || c.owner || c.approve },
    { id: 'diary', label: 'nav.projects.diary', path: '/projects/diary', can: (c) => c.site || c.owner || c.approve },
    { id: 'photos', label: 'nav.projects.photos', path: '/projects/photos', can: projects },
  ] },
  { id: 'office', label: 'nav.office', icon: 'office', items: [
    { id: 'attendance', label: 'nav.office.attendance', path: '/office/attendance', can: office },
    { id: 'wages', label: 'nav.office.wages', path: '/office/wages', can: wages },
    { id: 'workers', label: 'nav.office.workers', path: '/office/workers', can: people },
    { id: 'todos', label: 'nav.office.todos', path: '/office/todos', can: (c) => site(c) || c.owner },
    { id: 'visitors', label: 'nav.office.visitors', path: '/office/visitors', can: site },
  ] },
  { id: 'stores', label: 'nav.stores', icon: 'stores', items: [
    { id: 'stock', label: 'nav.stores.stock', path: '/stores/stock', can: store },
    { id: 'receive', label: 'nav.stores.receive', path: '/stores/receive', can: store },
    { id: 'issue', label: 'nav.stores.issue', path: '/stores/issue', can: store },
    { id: 'transfers', label: 'nav.stores.transfers', path: '/stores/transfers', can: store },
    { id: 'water', label: 'nav.stores.water', path: '/stores/water', can: store },
    { id: 'debris', label: 'nav.stores.debris', path: '/stores/debris', can: store },
  ] },
  { id: 'buy', label: 'nav.buy', icon: 'cart', items: [
    { id: 'requests', label: 'nav.buy.requests', path: '/buy/requests', can: buy },
    { id: 'quotations', label: 'nav.buy.quotations', path: '/buy/quotations', can: buy },
    { id: 'orders', label: 'nav.buy.orders', path: '/buy/orders', can: buy },
    { id: 'suppliers', label: 'nav.buy.suppliers', path: '/buy/suppliers', can: suppliers },
  ] },
  { id: 'money', label: 'nav.money', icon: 'money', items: [
    { id: 'budget', label: 'nav.money.budget', path: '/money/budget', can: money },
    { id: 'change-orders', label: 'nav.money.changes', path: '/money/change-orders', can: money },
    { id: 'invoices', label: 'nav.money.invoices', path: '/money/invoices', can: money },
    { id: 'bills', label: 'nav.money.bills', path: '/money/bills', can: money },
    { id: 'petty-cash', label: 'nav.money.petty', path: '/money/petty-cash', can: (c) => c.money || site(c) },
    { id: 'payments', label: 'nav.money.payments', path: '/money/payments', can: money },
    { id: 'tax', label: 'nav.money.tax', path: '/money/tax', can: money, later: true },
  ] },
  { id: 'clients', label: 'nav.clients', icon: 'clients', items: [
    { id: 'links', label: 'nav.clients.links', path: '/clients/links', can: clients },
    { id: 'selections', label: 'nav.clients.selections', path: '/clients/selections', can: clients, later: true },
    { id: 'enquiries', label: 'nav.clients.enquiries', path: '/clients/enquiries', can: clients, later: true },
  ] },
  { id: 'reports', label: 'nav.reports', icon: 'reports', items: [
    { id: 'progress', label: 'nav.reports.progress', path: '/reports/progress', can: reports },
    { id: 'cost', label: 'nav.reports.cost', path: '/reports/cost', can: (c) => c.money || c.owner },
    { id: 'stock', label: 'nav.reports.stock', path: '/reports/stock', can: (c) => reports(c) && (c.store || c.owner || c.money) },
    { id: 'attendance', label: 'nav.reports.attendance', path: '/reports/attendance', can: (c) => reports(c) && (c.people || c.money) },
    { id: 'export', label: 'nav.reports.export', path: '/reports/export', can: reports },
  ] },
  { id: 'settings', label: 'nav.settings', icon: 'settings', items: [
    { id: 'company', label: 'nav.settings.company', path: '/settings/company', can: settings },
    { id: 'people', label: 'nav.settings.people', path: '/settings/people', can: settings },
    { id: 'templates', label: 'nav.settings.templates', path: '/settings/templates', can: (c) => c.settings || c.money },
    { id: 'plan', label: 'nav.settings.plan', path: '/settings/plan', can: settings },
    { id: 'export', label: 'nav.settings.export', path: '/settings/export', can: settings },
  ] },
];

/** Menus and items the person may use; empty menus disappear. */
export function visibleMenus(caps: Caps): Menu[] {
  return MENUS.map((m) => ({ ...m, items: m.items.filter((i) => !i.can || i.can(caps)) })).filter((m) => m.items.length > 0);
}

/** Pages outside the side menu and who may open them. */
const EXTRA: { prefix: string; can: Rule }[] = [
  { prefix: '/approvals', can: any },
  { prefix: '/gate', can: office },
  { prefix: '/scan', can: any },
  { prefix: '/m/owner', can: (c) => c.owner || c.approve },
];

export function canOpenPath(caps: Caps, path: string): boolean {
  const ex = EXTRA.find((e) => path === e.prefix || path.startsWith(e.prefix + '/'));
  if (ex) return ex.can(caps);
  for (const m of MENUS) for (const i of m.items) if (path === i.path || path.startsWith(i.path + '/')) return !i.can || i.can(caps);
  return true;
}

export function findItem(path: string): { menu: Menu; item: MenuItem } | null {
  for (const m of MENUS) for (const i of m.items) if (path === i.path || path.startsWith(i.path + '/')) return { menu: m, item: i };
  return null;
}

/** Role-based start page (SPEC §6). `phone` picks the phone landing for owners and site supervisors. */
export function landingFor(role: Role, phone: boolean): string {
  switch (role) {
    case 'owner':
      return phone ? '/m/owner' : '/home/overview';
    case 'owner_supervisor':
      return '/approvals';
    case 'site_supervisor':
      return phone ? '/home/today' : '/office/attendance';
    case 'store_keeper':
      return '/stores/stock';
    case 'accountant':
      return '/money/bills';
    case 'partner':
      return '/projects/all';
    default:
      return '/home/overview';
  }
}
